// Postcode helpers shared by the homepage availability check, the
// services-page check, and the booking-form address autocomplete.
// Single source of truth so the three gates can't drift out of sync.

// London + South East England + Home Counties + counties adjacent to
// London on every side. Widen/narrow here and everything follows.
export const SE_UK_POSTCODE_AREAS = new Set([
  // London
  'E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC',
  // South East England (ONS region) - Surrey, Kent, Sussex, Hants, Oxon,
  // Berks, Bucks, IoW, plus the SE-coded Greater London fringe.
  'BN', 'BR', 'CR', 'CT', 'DA', 'GU', 'HA', 'HP', 'KT', 'ME', 'MK', 'OX',
  'PO', 'RG', 'RH', 'SG', 'SL', 'SM', 'SO', 'SS', 'TN', 'TW', 'UB', 'WD',
  // Home counties + counties adjacent to London on the N / NE / E
  // (Herts, Beds, Essex, Cambs, Northants).
  'AL', 'EN', 'LU', 'CM', 'CO', 'IG', 'RM', 'CB', 'NN'
])

// Friendly town-name → postcode-area map. Lets customers type their
// town instead of a postcode and still pass the availability gate.
// Keys are uppercased, whitespace-collapsed; lookups normalise the
// input the same way.
export const TOWN_TO_AREA = {
  // London (just a sentinel - any London area works for the gate)
  LONDON: 'SW',
  // Hertfordshire
  STALBANS: 'AL', ENFIELD: 'EN', WATFORD: 'WD', STEVENAGE: 'SG',
  HEMELHEMPSTEAD: 'HP', HEMEL: 'HP',
  // Bedfordshire
  LUTON: 'LU',
  // Essex
  CHELMSFORD: 'CM', COLCHESTER: 'CO', ILFORD: 'IG', ROMFORD: 'RM',
  SOUTHEND: 'SS', SOUTHENDONSEA: 'SS', BASILDON: 'SS', BRENTWOOD: 'CM',
  // Cambridgeshire
  CAMBRIDGE: 'CB',
  // Northamptonshire
  NORTHAMPTON: 'NN',
  // Buckinghamshire
  MILTONKEYNES: 'MK', AYLESBURY: 'HP', HIGHWYCOMBE: 'HP',
  // Berkshire
  READING: 'RG', SLOUGH: 'SL', BRACKNELL: 'RG', WINDSOR: 'SL',
  // Surrey
  GUILDFORD: 'GU', CROYDON: 'CR', SUTTON: 'SM', KINGSTON: 'KT',
  REDHILL: 'RH', REIGATE: 'RH', WOKING: 'GU',
  // Outer London / Middlesex
  HARROW: 'HA', UXBRIDGE: 'UB', TWICKENHAM: 'TW', BROMLEY: 'BR',
  // Kent
  DARTFORD: 'DA', MEDWAY: 'ME', MAIDSTONE: 'ME', CANTERBURY: 'CT',
  TUNBRIDGEWELLS: 'TN', TONBRIDGE: 'TN',
  // Hampshire
  PORTSMOUTH: 'PO', SOUTHAMPTON: 'SO',
  // Sussex
  BRIGHTON: 'BN', HOVE: 'BN', WORTHING: 'BN',
  // Oxfordshire
  OXFORD: 'OX'
}

// Normalise a user-typed string to a comparison key: uppercase,
// strip ALL non-alphabetics. So "St. Albans", "st-albans", "ST ALBANS"
// all become "STALBANS".
const normaliseTown = text => String(text || '').toUpperCase().replace(/[^A-Z]/g, '')

// Match a UK postcode anywhere in a string and return the postcode area
// (the letter prefix before the digit). e.g.
//   "10 Downing St, London SW1A 2AA" -> "SW"
//   "SW18 2DD"                       -> "SW"
//   "SW182DD"                        -> "SW"  (no space variant)
//   "not a postcode"                 -> null
// The full UK postcode in a piece of text ("SW6 1RE"), or null.
export const extractFullPostcode = text => {
  if (!text) return null
  const m = String(text).toUpperCase().match(/\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/)
  return m ? `${m[1]} ${m[2]}` : null
}

export const extractPostcodeArea = text => {
  if (!text) return null
  const match = String(text).toUpperCase().match(/\b([A-Z]{1,2})\d[A-Z\d]?\s*\d[A-Z]{2}\b/)
  return match ? match[1] : null
}

// Looser variant for the homepage / services page where the user might
// type any of: a full postcode, an outward part (SW18), a bare area
// (CM), or a town name (Chelmsford). Returns the area code or null.
export const extractPostcodeAreaLoose = text => {
  if (!text) return null
  const trimmed = String(text).trim().toUpperCase()
  if (!trimmed) return null

  // 1. Full postcode anywhere in the string? Take it (covers full
  //    addresses like "10 Downing St, London SW1A 2AA").
  const full = extractPostcodeArea(trimmed)
  if (full) return full

  // 2. Outward portion: letters followed by digit (SW18, CM1, EC1A).
  const outward = trimmed.match(/^([A-Z]{1,2})\d/)
  if (outward) return outward[1]

  // 3. Bare area letters only (CM, AL, SW). Limit to 1-2 letters so we
  //    don't catch random words.
  const bareArea = trimmed.match(/^([A-Z]{1,2})$/)
  if (bareArea) return bareArea[1]

  // 4. Town name (Chelmsford, Luton, St Albans, etc.) - normalise the
  //    input (strip punctuation/whitespace) and look up the curated map.
  const town = normaliseTown(trimmed)
  if (town && TOWN_TO_AREA[town]) return TOWN_TO_AREA[town]

  return null
}

// True when a free-text string looks like (or names) somewhere whose postcode
// area sits inside SE_UK_POSTCODE_AREAS. We only HARD-block strings we can
// positively resolve to an OUT-OF-AREA postcode; anything we can't resolve is
// let through (returns true) so a covered-but-unrecognised town/postcode isn't
// turned away at the homepage. The address picker downstream filters
// suggestions by area, so genuinely out-of-area users still can't complete.
export const isPostcodeInServiceArea = text => {
  const area = extractPostcodeAreaLoose(text)
  if (!area) return true
  return SE_UK_POSTCODE_AREAS.has(area)
}

// ---------------------------------------------------------------------------
// The typed postcode field (components/book-address BookPostcode).
//
// A UK postcode is the one piece of an address a customer can get exactly
// right, so the field must not argue about how they typed it: lower case,
// no space, extra spaces are all fine. We normalise to the canonical
// "N6 5UQ" form and refuse only something that is not a postcode at all.
// The same normalisation runs again server-side in Order#capture_postcode,
// so anything that slips past here still lands tidy in orders.postcode.
// ---------------------------------------------------------------------------

// A whole string that is a UK postcode and nothing else, space optional.
const FULL_POSTCODE_ONLY = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/

// Letters and digits only: spaces (non-breaking ones from a paste too), a
// hyphen or a full stop do not make a postcode wrong.
const squash = text => String(text || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '')

// The inward code (the last three) always starts with a digit, so an O or an
// I typed there is a 0 or a 1 ("SW6 IRE").
const fixInward = s => (s.length >= 5 ? s.slice(0, -3) + s.slice(-3).replace(/^[OI]/, c => (c === 'O' ? '0' : '1')) : s)

// The postcode in what was typed, canonical ("N6 5UQ"), or null. The whole
// box as a postcode first; then a postcode inside longer text, which is what
// a browser's autofill or a paste of an address line puts there
// ("SW6 1RE, London").
const postcodeIn = text => {
  const m = fixInward(squash(text)).match(FULL_POSTCODE_ONLY)
  if (m) return `${m[1]} ${m[2]}`
  return extractFullPostcode(text)
}

// "n65uq" / " N6  5uq " / "SW6 1RE, London" -> "N6 5UQ" / "SW6 1RE".
// Anything that is not a postcode comes back trimmed and uppercased, so the
// field never eats what was typed.
export const normalisePostcode = text => postcodeIn(text) || String(text || '').trim().toUpperCase()

// Is there a full UK postcode here, however it was spaced, cased or wrapped?
export const isFullPostcode = text => !!postcodeIn(text)

// The address string the order carries: what was typed, then the postcode
// once. A customer who also typed the postcode into the address used to get
// it twice ("... W11 2AB, W11 2AB", order 51808), so every copy of it is
// taken out of the typed text first, however it was spaced or cased.
// Order#capture_postcode collapses repeats again on the server.
export const addressWithPostcode = (address, postcode) => {
  const typed = String(address || '').trim()
  if (!isFullPostcode(postcode)) {
    return [typed, String(postcode || '').trim()].filter(Boolean).join(', ')
  }
  const pc = normalisePostcode(postcode)
  const [outward, inward] = pc.split(' ')
  const street = typed
    .replace(new RegExp(`\\b${outward}\\s*${inward}\\b`, 'gi'), '')
    .replace(/\s*,(\s*,)+/g, ',')
    .replace(/\s+,/g, ',')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(/^,\s*/, '')
    .replace(/,$/, '')
    .trim()
  return [street, pc].filter(Boolean).join(', ')
}

// ---------------------------------------------------------------------------
// Congestion Charge Zone, by postal district. Book & pay shows the "Inside
// the Congestion Charge Zone?" tick only for a postcode whose district can be
// in the zone (ruling of 9 Oct 2026, booking-screen-trim section 1). Deciding
// yes or no exactly needs the zone polygon, which is the geocoding ruled out
// on 4 Oct; the district list is the honest halfway, and the customer ticks.
export const CONGESTION_DISTRICTS = new Set(['EC1', 'EC2', 'EC3', 'EC4', 'WC1', 'WC2', 'SW1', 'SE1', 'W1', 'N1', 'E1', 'NW1'])

// "SW1V 3AA" -> "SW1", "W10 5AB" -> "W10", "EC1A 1BB" -> "EC1"; null when
// there is no full postcode to read.
export const postcodeDistrict = text => {
  if (!isFullPostcode(text)) return null
  const outward = normalisePostcode(text).split(' ')[0] || ''
  const m = outward.match(/^([A-Z]{1,2})(\d{1,2})[A-Z]?$/)
  return m ? `${m[1]}${m[2]}` : null
}

export const mayBeInCongestionZone = text => CONGESTION_DISTRICTS.has(postcodeDistrict(text))
